import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { combinarDataHora } from './utils';
import type { FormaParticipar } from './types';

export interface FalhaParticipacao {
  indice: number;
  nome: string;
  mensagem: string;
}

// Erro com a lista do que falhou, para a tela dizer qual ingresso/inscrição
// não foi salvo (e por quê) em vez de parar tudo sem avisar.
export class ErroSalvarParticipacoes extends Error {
  falhas: FalhaParticipacao[];
  lista: FormaParticipar[];

  constructor(falhas: FalhaParticipacao[], lista: FormaParticipar[] = []) {
    super(
      falhas
        .map(
          (f) =>
            `${f.nome || `Item ${f.indice + 1}`}: ${f.mensagem}`,
        )
        .join(' · '),
    );
    this.name = 'ErroSalvarParticipacoes';
    this.falhas = falhas;
    this.lista = lista;
  }
}

// Grava a lista inteira de tipos de inscrição (modalidades) de um evento.
// Reutilizado pela tela de criar e pelo painel de editar — mesma regra, um só lugar.
export async function salvarParticipacoes(
  eventoId: number,
  participacoes: FormaParticipar[],
): Promise<FormaParticipar[]> {
  const novaLista = participacoes.map((p) => ({
    ...p,
    grupos: p.grupos.map((g) => ({ ...g })),
  }));
  const falhas: FalhaParticipacao[] = [];

  for (let i = 0; i < novaLista.length; i += 1) {
    const p = novaLista[i];
    try {
      const dados: Record<string, unknown> = {
        evento_id: eventoId,
        nome: p.nome.trim(),
        descricao: p.descricao || null,
        publica: p.publica,
        codigo_acesso: p.publica ? null : p.codigoAcesso || null,
        inscricoes_abrem_em: combinarDataHora(p.inscricoesAbremData, p.inscricoesAbremHora),
        inscricoes_fecham_em: combinarDataHora(p.inscricoesFechamData, p.inscricoesFechamHora),
        exige_identificacao: p.exigeIdentificacao,
        max_por_compra: p.maxPorCompra,
        validacao_presencial: p.validacaoPresencial,
        ordem: i,
        vagas_total: p.temLimite ? p.vagas : null,
        valor_definido: p.valorModo !== 'a_definir',
      };
      if (p.valorModo !== 'a_definir') {
        dados.valor_centavos = p.dividirGrupos ? 0 : p.valorCentavos;
      }

      let idModalidade = p.id;
      if (idModalidade) {
        const { error } = await supabase.from('modalidades').update(dados).eq('id', idModalidade);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('modalidades')
          .insert({ ...dados, valor_centavos: (dados.valor_centavos as number) ?? 0 })
          .select('id')
          .single();
        if (error) throw error;
        idModalidade = (data as { id: number }).id;
        novaLista[i] = { ...p, id: idModalidade };
      }

      // Grupos (opções).
      const idsAtuais: number[] = [];
      for (let j = 0; j < p.grupos.length; j += 1) {
        const g = p.grupos[j];
        const gdados = {
          nome: g.nome.trim(),
          vagas: g.vagas,
          valor_centavos: g.valorCentavos,
          ordem: j,
        };
        if (g.id) {
          const { error } = await supabase.from('opcoes').update(gdados).eq('id', g.id);
          if (error) throw error;
          idsAtuais.push(g.id);
        } else {
          const { data, error } = await supabase
            .from('opcoes')
            .insert({ ...gdados, modalidade_id: idModalidade })
            .select('id')
            .single();
          if (error) throw error;
          const novoId = (data as { id: number }).id;
          p.grupos[j] = { ...g, id: novoId };
          idsAtuais.push(novoId);
        }
      }

      // Remove (ou desativa) grupos que saíram da lista.
      const { data: existentes } = await supabase
        .from('opcoes')
        .select('id')
        .eq('modalidade_id', idModalidade);
      const idsExistentes = ((existentes as { id: number }[]) || []).map((e) => e.id);
      const removidas = idsExistentes.filter((id) => !idsAtuais.includes(id));
      if (removidas.length > 0) {
        const { data: insc } = await supabase
          .from('inscricoes')
          .select('opcao_id')
          .in('opcao_id', removidas);
        const comInscrito = new Set(
          ((insc as { opcao_id: number }[]) || []).map((r) => r.opcao_id),
        );
        const paraDeletar = removidas.filter((id) => !comInscrito.has(id));
        const paraDesativar = removidas.filter((id) => comInscrito.has(id));
        if (paraDeletar.length > 0) {
          const { error } = await supabase.from('opcoes').delete().in('id', paraDeletar);
          if (error) throw error;
        }
        if (paraDesativar.length > 0) {
          const { error } = await supabase
            .from('opcoes')
            .update({ ativa: false })
            .in('id', paraDesativar);
          if (error) throw error;
        }
      }
    } catch (e) {
      falhas.push({
        indice: i,
        nome: p.nome.trim(),
        mensagem: mensagemAmigavel(e, 'Não foi possível salvar. Tente novamente.'),
      });
    }
  }

  if (falhas.length > 0) throw new ErroSalvarParticipacoes(falhas, novaLista);

  return novaLista;
}